import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { getDesktopBridge } from '@/desktop';
import { accountReturnPath, accountSettingsPageAvailable, isAccountSettingsRoute } from '@/utils/account-settings-navigation';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    requiresAuth?: boolean;
    standaloneComponent?: boolean;
    workspace?: boolean;
    desktopOnly?: boolean;
    storefront?: boolean;
    marketplaceGroup?: 'components' | 'agent_capabilities' | 'appearance';
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'workspace-entry',
    component: () => import('@/views/WorkspaceEntryView.vue'),
    meta: { requiresAuth: true, title: '我的工作台' },
  },
  {
    path: '/auth',
    component: () => import('@/views/layouts/AuthLayout.vue'),
    meta: { title: '账号' },
    children: [
      { path: '', redirect: '/auth/login' },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { title: '登录' },
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: { title: '注册' },
      },
    ],
  },
  {
    path: '/legal/:documentKey?',
    name: 'legal-document',
    component: () => import('@/views/LegalDocumentView.vue'),
    props: (route) => ({ documentKey: route.params.documentKey }),
    meta: { title: '法律与隐私' },
  },
  {
    path: '/workspaces',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'workspace-management',
        component: () => import('@/views/WorkspaceManagementView.vue'),
        meta: { title: '管理工作台' },
      },
      {
        path: 'manage/:workspaceId?/regional',
        redirect: { name: 'account-profile-settings' },
      },
      {
        path: 'manage/:workspaceId?/appearance',
        redirect: { name: 'account-appearance-settings' },
      },
      {
        path: 'manage/:workspaceId?/:section(general|preferences|members|runtime|danger)?',
        redirect: (to) => ({
          name: 'workspace-management',
          query: {
            ...(typeof to.params.workspaceId === 'string'
              ? { workspaceId: to.params.workspaceId }
              : {}),
            ...(typeof to.params.section === 'string' && to.params.section !== 'general'
              ? { section: to.params.section }
              : {}),
          },
        }),
      },
    ],
  },
  {
    path: '/studio',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true, desktopOnly: true },
    children: [
      {
        path: '',
        name: 'developer-workspace',
        component: () => import('@/views/DeveloperWorkspaceView.vue'),
        meta: { title: '开发工作台' },
      },
      {
        path: 'components',
        name: 'component-list',
        redirect: { name: 'developer-workspace', query: { type: 'component' } },
      },
      {
        path: 'assets',
        name: 'studio-asset-publish',
        redirect: { name: 'developer-workspace', query: { type: 'skill' } },
      },
      {
        path: 'assets/:draftId/:section(edit|versions)?',
        name: 'capability-detail',
        component: () => import('@/views/CapabilityDetailView.vue'),
        meta: { title: '能力编辑' },
      },
      // 保留旧商店地址，避免既有书签失效；商店不再属于 Studio。
      { path: 'component-store', redirect: { name: 'component-marketplace' } },
      {
        path: 'components/new',
        name: 'component-new',
        component: () => import('@/views/ComponentCreateView.vue'),
        meta: { title: '创建组件' },
      },
      {
        path: 'components/:componentId/data',
        name: 'component-data-management',
        component: () => import('@/views/ComponentDataManagementView.vue'),
        meta: { title: '组件数据管理' },
      },
      // 兼容旧列表与已复制的草稿链接；当前编辑器的默认入口是预览页。
      {
        path: 'components/:componentId/draft',
        redirect: (to) => ({
          name: 'component-detail',
          params: { componentId: to.params.componentId, section: 'preview' },
        }),
      },
      // 套装配置已归入“高级”，保留旧链接以兼容已复制的地址。
      {
        path: 'components/:componentId/suite',
        redirect: (to) => ({
          name: 'component-detail',
          params: { componentId: to.params.componentId, section: 'advanced' },
          query: to.query,
        }),
      },
      {
        path: 'components/:componentId/feedback',
        redirect: (to) => ({
          name: 'component-detail',
          params: { componentId: to.params.componentId, section: 'versions' },
          query: to.query,
        }),
      },
      {
        path: 'components/:componentId/:section(preview|source|assets|functions|manifest|agent|permissions|advanced|memory|public-data|versions)?',
        name: 'component-detail',
        component: () => import('@/views/ComponentDetailView.vue'),
        meta: { title: '组件编辑器' },
      },
      {
        path: 'publications/:publicationId/data',
        name: 'component-publication-analytics',
        component: () => import('@/views/ComponentPublicationAnalyticsView.vue'),
        meta: { title: '发布数据统计' },
      },
      {
        path: 'marketplace/:packageId/operations',
        name: 'marketplace-asset-operations',
        component: () => import('@/views/ComponentPublicationAnalyticsView.vue'),
        meta: { title: '资产运营中心' },
      },
    ],
  },
  {
    path: '/marketplace',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true, storefront: true },
    children: [
      {
        path: '',
        name: 'capability-marketplace',
        component: () => import('@/views/MarketplaceView.vue'),
        meta: { title: '灵犀商店' },
      },
      {
        path: 'components',
        name: 'marketplace-components',
        component: () => import('@/views/MarketplaceView.vue'),
        meta: { title: '组件 · 灵犀商店', marketplaceGroup: 'components' },
      },
      {
        path: 'agent',
        name: 'marketplace-agent-capabilities',
        component: () => import('@/views/MarketplaceView.vue'),
        meta: { title: 'Agent 能力 · 灵犀商店', marketplaceGroup: 'agent_capabilities' },
      },
      {
        path: 'themes',
        name: 'marketplace-themes',
        component: () => import('@/views/MarketplaceView.vue'),
        meta: { title: '主题 · 灵犀商店', marketplaceGroup: 'appearance' },
      },
      {
        path: 'demands',
        name: 'marketplace-demands',
        component: () => import('@/views/MarketplaceDemandListView.vue'),
        meta: { title: '需求广场' },
      },
      {
        path: 'demands/new',
        name: 'marketplace-demand-new',
        component: () => import('@/views/MarketplaceDemandFormView.vue'),
        meta: { title: '发布需求' },
      },
      {
        path: 'demands/:demandId/edit',
        name: 'marketplace-demand-edit',
        component: () => import('@/views/MarketplaceDemandFormView.vue'),
        meta: { title: '修改需求' },
      },
      {
        path: 'demands/:demandId',
        name: 'marketplace-demand-detail',
        component: () => import('@/views/MarketplaceDemandDetailView.vue'),
        meta: { title: '需求详情' },
      },
      {
        path: ':packageId',
        name: 'capability-marketplace-detail',
        component: () => import('@/views/MarketplaceDetailView.vue'),
        meta: { title: '能力详情' },
      },
    ],
  },
  {
    path: '/component-marketplace',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'component-marketplace',
        redirect: { name: 'marketplace-components' },
      },
    ],
  },
  {
    path: '/library',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'asset-library',
        component: () => import('@/views/PersonalAssetsView.vue'),
        meta: { title: '我的组件/资产' },
      },
      {
        path: 'creator/earnings',
        name: 'creator-earnings',
        component: () => import('@/views/CreatorEarningsView.vue'),
        meta: { title: '创作者收益' },
      },
      {
        path: 'components/:installationId/data',
        name: 'installation-data-management',
        component: () => import('@/views/ComponentDataManagementView.vue'),
        meta: { title: '组件数据管理' },
      },
    ],
  },
  {
    path: '/files',
    name: 'account-files',
    component: () => import('@/views/AccountFilesView.vue'),
    meta: { requiresAuth: true, title: '文件' },
  },
  {
    path: '/knowledge',
    name: 'account-knowledge',
    component: () => import('@/views/AccountKnowledgeView.vue'),
    meta: { requiresAuth: true, title: '知识库' },
  },
  {
    path: '/creative',
    name: 'creative-center',
    component: () => import('@/views/CreativeCenterView.vue'),
    meta: { requiresAuth: true, title: '创意中心' },
  },
  {
    path: '/creative/create',
    name: 'creative-create',
    component: () => import('@/views/CreativeCenterView.vue'),
    meta: { requiresAuth: true, title: '创作' },
  },
  {
    path: '/component-library',
    component: () => import('@/views/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'component-library',
        redirect: { name: 'asset-library', query: { type: 'component' } },
      },
      {
        path: 'link/:token',
        name: 'component-publication-link',
        component: () => import('@/views/ComponentPublicationLinkView.vue'),
        meta: { title: '共享组件' },
      },
    ],
  },
  {
    path: '/tasks',
    component: () => import('@/views/layouts/WorkspaceLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: ':taskId?',
        name: 'workspace-tasks',
        component: () => import('@/views/TaskManagementView.vue'),
        meta: { title: '任务' },
      },
    ],
  },
  {
    path: '/w/:workspaceId/tasks/:taskId?',
    redirect: (to) => ({
      name: 'workspace-tasks',
      params: { taskId: to.params.taskId },
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: '/w/:workspaceId/artifacts/:sessionId/:messageId/:artifactId',
    name: 'agent-presentation-artifact',
    component: () => import('@/views/AgentPresentationArtifactView.vue'),
    meta: { requiresAuth: true, workspace: true, title: 'Agent 展示成果' },
  },
  {
    path: '/w/:workspaceId/pages/:pageId/apps/:instanceId',
    name: 'workspace-component-app',
    component: () => import('@/views/WorkspaceComponentAppView.vue'),
    meta: { requiresAuth: true, standaloneComponent: true, title: '组件轻应用' },
  },
  {
    path: '/w/:workspaceId',
    component: () => import('@/views/layouts/WorkspaceLayout.vue'),
    meta: { requiresAuth: true, workspace: true },
    children: [
      {
        path: '',
        name: 'workspace-dashboard',
        component: () => import('@/views/WorkspacePageRedirectView.vue'),
        meta: { title: '我的工作台' },
      },
      {
        path: 'pages',
        name: 'workspace-pages',
        redirect: (to) => ({
          name: 'workspace-dashboard',
          params: { workspaceId: String(to.params.workspaceId) },
        }),
        meta: { title: '工作区页面' },
      },
      {
        path: 'pages/:pageId',
        name: 'workspace-page',
        component: () => import('@/views/WorkspaceCanvasView.vue'),
        meta: { title: '页面画布' },
      },
      {
        path: 'catalog',
        name: 'workspace-catalog',
        redirect: { name: 'component-marketplace' },
        meta: { title: '组件目录' },
      },
      {
        path: 'components/:installationId/data',
        redirect: (to) => ({
          name: 'installation-data-management',
          params: { installationId: to.params.installationId },
        }),
      },
      {
        path: 'components',
        redirect: { name: 'component-library' },
      },
      // 工作区内不再单独维护“已安装组件”页面，旧链接统一回到个人组件库。
      {
        path: 'installed/:installationId/data',
        redirect: (to) => ({
          name: 'installation-data-management',
          params: { installationId: to.params.installationId },
        }),
      },
      {
        path: 'installed/:installationId?',
        redirect: { name: 'component-library' },
      },
      {
        path: 'chat',
        name: 'workspace-chat',
        component: () => import('@/views/AgentWorkspaceView.vue'),
        props: (route) => ({
          home: true,
          agentId: typeof route.query.agentId === 'string' ? route.query.agentId : null,
        }),
        meta: { title: '主控会话' },
      },
      // 兼容已分享的旧会话链接，实际页面只保留 chat 路由。
      {
        path: 'chat/:sessionId',
        redirect: (to) => ({
          name: 'workspace-chat',
          params: { workspaceId: String(to.params.workspaceId) },
          query: { ...to.query, sessionId: String(to.params.sessionId) },
        }),
      },
      {
        path: 'data/:schemaId?',
        name: 'workspace-data',
        redirect: { name: 'component-library' },
        meta: { title: '数据' },
      },
      {
        path: 'settings/sandbox',
        redirect: (to) => ({
          name: 'workspace-management',
          query: { ...to.query, workspaceId: String(to.params.workspaceId), section: 'runtime' },
          hash: to.hash,
        }),
      },
      { path: 'settings/models', redirect: { name: 'account-model-settings' } },
      {
        path: 'settings/regional',
        redirect: { name: 'account-profile-settings' },
      },
      {
        path: 'settings/appearance',
        redirect: { name: 'account-appearance-settings' },
      },
      {
        path: 'settings/:section(general|preferences|members|runtime|danger)?',
        name: 'workspace-settings',
        redirect: (to) => ({
          name: 'workspace-management',
          query: {
            ...to.query,
            workspaceId: String(to.params.workspaceId),
            section: typeof to.params.section === 'string' && to.params.section ? to.params.section : 'general',
          },
          hash: to.hash,
        }),
      },
    ],
  },
  {
    path: '/notifications',
    component: () => import('@/views/layouts/AccountSettingsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'notification-center',
        component: () => import('@/views/NotificationCenterView.vue'),
        meta: { title: '通知中心' },
      },
    ],
  },
  {
    path: '/settings',
    component: () => import('@/views/layouts/AccountSettingsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'account-settings', component: () => import('@/views/AccountSettingsHomeView.vue'), meta: { title: '个人设置' } },
      {
        path: 'profile',
        name: 'account-profile-settings',
        component: () => import('@/views/AccountProfileSettingsView.vue'),
        meta: { title: '个人资料' },
      },
      {
        path: 'appearance',
        name: 'account-appearance-settings',
        component: () => import('@/views/AccountAppearanceSettingsView.vue'),
        meta: { title: '外观主题' },
      },
      {
        path: 'notifications',
        name: 'account-notification-settings',
        component: () => import('@/views/AccountNotificationSettingsView.vue'),
        meta: { title: '通知设置' },
        beforeEnter: (to) => accountSettingsPageAvailable('account-notification-settings') || { name: 'account-settings', query: to.query },
      },
      {
        path: 'themes/new',
        name: 'theme-studio',
        component: () => import('@/views/ThemeStudioView.vue'),
        meta: { title: '主题创作' },
      },
      {
        path: 'security',
        name: 'account-security-settings',
        component: () => import('@/views/AccountSecuritySettingsView.vue'),
        meta: { title: '登录安全' },
      },
      {
        path: 'privacy',
        name: 'account-privacy-settings',
        component: () => import('@/views/AccountPrivacySettingsView.vue'),
        meta: { title: '隐私与数据' },
      },
      {
        path: 'models',
        name: 'account-model-settings',
        component: () => import('@/views/WorkspaceModelSettingsView.vue'),
        meta: { title: '大脑设置' },
      },
      {
        path: 'orders',
        name: 'account-orders',
        component: () => import('@/views/CommerceOrdersView.vue'),
        meta: { title: '套餐、用量与订单' },
      },
      {
        path: 'credits',
        name: 'account-credits',
        redirect: (to) => ({ name: 'account-orders', query: { ...to.query, tab: 'credits' } }),
      },
      {
        path: 'rewards',
        name: 'account-rewards',
        component: () => import('@/views/GrowthRewardsView.vue'),
        meta: { title: '奖励中心' },
      },
      {
        path: 'coupons',
        name: 'account-coupons',
        component: () => import('@/views/CouponCenterView.vue'),
        meta: { title: '卡券中心' },
      },
      {
        path: 'connectors',
        name: 'account-connectors',
        component: () => import('@/views/AccountConnectorSettingsView.vue'),
        meta: { title: '连接器' },
      },
    ],
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/StatusView.vue'),
    props: { code: '403', title: '无权访问此资源' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/StatusView.vue'),
    props: { code: '404', title: '页面不存在' },
  },
];

const router = createRouter({
  history: getDesktopBridge() ? createWebHashHistory() : createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const auth = useAuthStore();
  if (!auth.initialized) await auth.initialize();
  if (auth.initializationError) return false;
  if (to.meta.requiresAuth && !auth.isAuthenticated)
    return { name: 'login', query: { redirect: to.fullPath } };
  if (to.meta.requiresAuth && auth.isAuthenticated && !to.meta.standaloneComponent) {
    const workspace = useWorkspaceStore();
    if (!workspace.initialized) await workspace.refreshAll();
  }
  if ((to.name === 'creative-center' || to.name === 'creative-create') && auth.user?.creativeCenterEnabled !== true)
    return { name: 'not-found' };
  if (to.name === 'workspace-management' && to.query.section === 'regional')
    return { name: 'account-profile-settings' };
  if (to.name === 'workspace-management' && to.query.section === 'appearance')
    return { name: 'account-appearance-settings' };
  if (to.path.startsWith('/auth') && auth.isAuthenticated) return '/';
  if ((isAccountSettingsRoute(to.path) || to.path === '/notifications') && !to.query.returnTo) {
    const returnTo = accountReturnPath(router, from.query.returnTo) || accountReturnPath(router, from.fullPath);
    if (returnTo) return { path: to.path, query: { ...to.query, returnTo }, hash: to.hash };
  }
  document.title = getDesktopBridge() ? '灵犀工坊' : `${to.meta.title ?? '灵犀工坊'} · 灵犀工坊`;
  return true;
});

router.afterEach((to, _from, failure) => {
  if (failure) return;
  void getDesktopBridge()?.window?.setPageMode?.(to.path.startsWith('/auth') ? 'auth' : 'app')
    .catch((error) => console.error('调整应用窗口失败', error));
});

export default router;
